import { apiJson } from '@/lib/http';
import { TeamRole } from '@/types/roles';

// Use shared helper
const apiCall = <T = unknown>(endpoint: string, options: RequestInit = {}) => apiJson<T>(endpoint, options);

// Backend API response types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Team types
export interface Team {
  id: string;
  name: string;
  adminEmail: string;
  dataRetentionPolicy: string;
  autoPurgeExpired: boolean;
  defaultTokenBudget: number;
  defaultSemanticWeight: number;
  complianceMode: boolean;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  activeMemberCount: number;
  pendingMemberCount: number;
  subscription: number;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  googleCalendarId?: string;
}

export interface SuperAdminTeamOption {
  id: string;
  name: string;
}

export interface Profile {
  id: string;
  name: string;
  email: string;
  teamRole: TeamRole;
  createdAt: string;
  updatedAt: string;
  onboardingStep: string;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: TeamRole;
  status: 'active' | 'pending' | 'suspended';
  lastActive?: string;
  joinedAt?: string;
  createdAt: string;
}

export interface TeamInvite {
  id: string;
  teamId: string;
  email: string;
  role: TeamRole;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

export interface UpdateTeamRequest {
  name?: string;
  adminEmail?: string;
  dataRetentionPolicy?: string;
  autoPurgeExpired?: boolean;
  defaultTokenBudget?: number;
  defaultSemanticWeight?: number;
  complianceMode?: boolean;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  googleCalendarId?: string;
}

export interface InviteTeamMemberRequest {
  email: string;
  role: TeamRole;
}

export interface UpdateTeamMemberRequest {
  role?: TeamRole;
  status?: 'active' | 'pending' | 'suspended';
}

export interface TeamSettingDto {
  id?: string;
  type: string;
  content: string;
  createdAt?: string;
  updatedAt?: string;
}

export type TeamSettingUpsert = Pick<TeamSettingDto, 'type' | 'content'>;

export const teamService = {
  // Get Sophie teams available to super admin for team switching
  async getSophieTeamsForSuperAdmin(): Promise<SuperAdminTeamOption[]> {
    try {
      const response: ApiResponse<SuperAdminTeamOption[]> = await apiCall('/Teams/superadmin/sophie-teams');
      return response.data ?? [];
    } catch (error) {
      console.error('Error fetching Sophie teams for super admin:', error);
      throw error;
    }
  },

  // Get current user's team
  async getTeam(): Promise<Team> {
    try {
      const response: ApiResponse<Team> = await apiCall('/Teams');
      return response.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  },

  // Update team settings
  async updateTeam(request: UpdateTeamRequest): Promise<Team> {
    try {
      const response: ApiResponse<Team> = await apiCall('/Teams', {
        method: 'PUT',
        body: JSON.stringify(request),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating team:', error);
      throw error;
    }
  },

  // Get team members
  async getTeamMembers(): Promise<TeamMember[]> {
    try {
      const response: ApiResponse<TeamMember[]> = await apiCall('/Teams/members');
      return response.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  },

  // Invite team member
  async inviteTeamMember(request: InviteTeamMemberRequest): Promise<TeamMember> {
    try {
      const response: ApiResponse<TeamMember> = await apiCall('/Teams/members', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return response.data;
    } catch (error) {
      console.error('Error inviting team member:', error);
      throw error;
    }
  },

  // Update team member
  async updateTeamMember(teamUserId: string, request: UpdateTeamMemberRequest): Promise<TeamMember> {
    try {
      const response: ApiResponse<TeamMember> = await apiCall(`/Teams/members/${teamUserId}`, {
        method: 'PUT',
        body: JSON.stringify(request),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
  },

  // Remove team member
  async removeTeamMember(teamUserId: string): Promise<void> {
    try {
      await apiCall(`/Teams/members/${teamUserId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
  },
  // Update onboarding step
  async updateOnboardingStep(onboardingStep: string[]): Promise<void> {
    try {
      await apiCall(`/Teams/onboardingstep`, {
        method: 'POST',
        body: JSON.stringify({ onboardingStep: onboardingStep }),
      });
    } catch (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
  },


  // Get user role in team
  async getUserProfile(): Promise<Profile> {
    try {
      const response: ApiResponse<Profile> = await apiCall('/Teams/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  // Update user profile
  async updateUserProfile(name: string, phone?: string): Promise<Profile> {
    try {
      const response: ApiResponse<Profile> = await apiCall('/Teams/profile', {
        method: 'PUT',
        body: JSON.stringify({ name, phone }),
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Get team invites
  async getTeamInvites(): Promise<TeamInvite[]> {
    try {
      const response: ApiResponse<TeamInvite[]> = await apiCall('/Teams/invites');
      return response.data;
    } catch (error) {
      console.error('Error fetching team invites:', error);
      throw error;
    }
  },

  // Cancel invite
  async cancelInvite(inviteId: string): Promise<void> {
    try {
      await apiCall(`/Teams/invites/${inviteId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error canceling invite:', error);
      throw error;
    }
  },

  // Get team settings
  async getTeamSettings(): Promise<TeamSettingDto[]> {
    try {
      const response: ApiResponse<TeamSettingDto[] | TeamSettingDto> = await apiCall('/Teams/teamsettings');
      const data = response.data as unknown;
      if (Array.isArray(data)) {
        return data as TeamSettingDto[];
      }
      if (data && typeof data === 'object') {
        return [data as TeamSettingDto];
      }
      return [];
    } catch (error) {
      console.error('Error fetching team settings:', error);
      throw error;
    }
  },

  // Update team settings
  async updateTeamSettings(settings: TeamSettingUpsert[]): Promise<void> {
    try {
      await apiCall('/Teams/teamsettings', {
        method: 'POST',
        body: JSON.stringify(settings.map(s => ({ type: s.type, content: s.content })))
      });
    } catch (error) {
      console.error('Error updating team settings:', error);
      throw error;
    }
  },
}; 